import React, { createContext, useContext, useState, useEffect } from 'react'
import { client } from '../client'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check if dealer is already logged in on app start
    const checkAuthState = async () => {
      try {
        // Check for dealer token in both localStorage and sessionStorage
        const token =
          localStorage.getItem('dealer_token') ||
          sessionStorage.getItem('dealer_token')
        const dealerData =
          localStorage.getItem('dealer_info') ||
          sessionStorage.getItem('dealer_info')

        if (token && dealerData) {
          // Verify token with server
          try {
            const response = await client.get('/auth/verify')

            if (response.data.success) {
              // Token is valid, restore authentication state
              setIsAuthenticated(true)
              setUser(JSON.parse(dealerData))
              console.log(
                'Authentication restored for dealer:',
                JSON.parse(dealerData).dealerName
              )
            } else {
              // Token is invalid, clear auth data
              clearAuthData()
            }
          } catch (error) {
            console.error('Token verification failed:', error)
            // Token verification failed, clear auth data
            clearAuthData()
          }
        } else {
          // No token found, user is not authenticated
          setIsAuthenticated(false)
          setUser(null)
        }
      } catch (error) {
        console.error('Error checking auth state:', error)
        // Clear potentially corrupted data
        clearAuthData()
      } finally {
        setLoading(false)
      }
    }

    checkAuthState()
  }, [])

  const clearAuthData = () => {
    // Clear all authentication data
    localStorage.removeItem('dealer_token')
    localStorage.removeItem('dealer_info')
    sessionStorage.removeItem('dealer_token')
    sessionStorage.removeItem('dealer_info')

    // Also clear legacy auth data if exists
    localStorage.removeItem('isAuthenticated')
    localStorage.removeItem('user')

    setIsAuthenticated(false)
    setUser(null)
  }

  const login = (dealerData, token, remember = false) => {
    setIsAuthenticated(true)
    setUser(dealerData)

    // Store dealer data and token based on remember preference
    const storage = remember ? localStorage : sessionStorage

    if (token) {
      storage.setItem('dealer_token', token)
    }
    storage.setItem('dealer_info', JSON.stringify(dealerData))

    // For backward compatibility, also set legacy auth state
    storage.setItem('isAuthenticated', 'true')
    storage.setItem('user', JSON.stringify(dealerData))

    console.log('Dealer logged in:', dealerData.dealerName)
  }

  const logout = async () => {
    try {
      // Get token before clearing
      const token =
        localStorage.getItem('dealer_token') ||
        sessionStorage.getItem('dealer_token')

      if (token) {
        // Call logout API to invalidate token on server
        try {
          await client.post('/auth/logout')
        } catch (error) {
          console.error('Logout API call failed:', error)
          // Continue with local logout even if API call fails
        }
      }
    } catch (error) {
      console.error('Error during logout:', error)
    } finally {
      // Clear all authentication data
      clearAuthData()
      console.log('User logged out')
    }
  }

  const refreshToken = async () => {
    try {
      const token =
        localStorage.getItem('dealer_token') ||
        sessionStorage.getItem('dealer_token')

      if (!token) {
        throw new Error('No token found')
      }

      const response = await client.get('/auth/profile')

      if (response.data.success) {
        const updatedDealerData = {
          ...user,
          ...response.data.dealer,
          loginTime: user?.loginTime || new Date().toISOString(),
          role: 'dealer'
        }

        setUser(updatedDealerData)

        // Update stored dealer info
        const storage = localStorage.getItem('dealer_token')
          ? localStorage
          : sessionStorage
        storage.setItem('dealer_info', JSON.stringify(updatedDealerData))

        return updatedDealerData
      } else {
        throw new Error('Failed to refresh user data')
      }
    } catch (error) {
      console.error('Error refreshing token:', error)
      // If refresh fails, logout user
      logout()
      throw error
    }
  }

  const getToken = () => {
    return (
      localStorage.getItem('dealer_token') ||
      sessionStorage.getItem('dealer_token')
    )
  }

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
    loading,
    refreshToken,
    getToken,
    clearAuthData
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthContext
